import React, { useEffect, useState } from 'react';

import {
    Box,
    Button,
    Tab,
    Tabs,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import { useApiIsLoaded } from '@vis.gl/react-google-maps';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import CustomTabPanel from 'common/components/segment/CustomTabPanel';
import yupResolver from 'common/utils/yupResolver';

import createProposalModal from '../../../createProposalModal';
import * as multiPanelLayoutActions from '../../../multiPanelLayout/actions';
import * as actions from '../../actions';
import * as selectors from '../../selectors';

import { TabGroups, TabModule, TabRooftop } from './components';
import validate from './validateSegment';

const initInitialValues = ({ initialValues }) => {
    const { solar_modules_number, panel } = initialValues;
    let kW = 0;

    if (panel) {
        const { watts = 0 } = panel;
        kW = (solar_modules_number * watts) / 1000;
    }

    return { ...initialValues, panel, kW };
};

const SegmentEditForm = ({
    commercialOfferId,
    fetchPreset,
    hasGroupsConfigErrors,
    initialValues,
    onCloseDrawer,
    panels,
    resetForm,
    segment,
    setCurrentValues,
    updateSegment,
    fetchSegments,
}) => {
    const { t } = useTranslation();
    const [google, setGoogle] = useState(null);
    const apiIsLoaded = useApiIsLoaded();
    const [selectedTab, setSelectedTab] = useState('rooftop');
    const defaultValues = initInitialValues({ initialValues, panels });
    const { control, handleSubmit, reset, setValue, trigger, watch } = useForm({
        defaultValues,
        resolver: yupResolver(validate),
    });

    const handleOnChangeTab = (_, newValue) => setSelectedTab(newValue);

    const handleOnSave = (values) => {
        updateSegment({
            callback: () => {
                fetchSegments(commercialOfferId);
                onCloseDrawer();
                resetForm();
            },
            commercialOfferId,
            values,
        });
    };
    const [
        has_initial_height,
        initial_height,
        orientation,
        panel,
        presetId,
        row_spacing,
        system_uid,
        tilt,
        type_application,
    ] = watch([
        'has_initial_height',
        'initial_height',
        'orientation',
        'panel',
        'preset_configuration',
        'row_spacing',
        'system_uid',
        'tilt',
        'type_application',
    ]);

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    useEffect(() => {
        const defaultValues = initInitialValues({ initialValues, panels });
        reset(defaultValues);
    }, [initialValues]);

    useEffect(() => {
        setCurrentValues({
            has_initial_height,
            initial_height,
            orientation,
            panel: { ...panel },
            row_spacing,
            system_uid,
            tilt,
            type_application,
        });

        return () => {
            setCurrentValues({});
        };
    }, [
        has_initial_height,
        initial_height,
        orientation,
        panel,
        row_spacing,
        system_uid,
        tilt,
        type_application,
    ]);

    useEffect(() => {
        if (presetId) fetchPreset(presetId);
    }, [presetId]);

    return (
        <Box>
            <form onSubmit={handleSubmit(handleOnSave)}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        noWrap
                        sx={{
                            flexGrow: 1,
                            minWidth: 0,
                            color: 'text.primary',
                            alignSelf: 'flex-start',
                        }}
                    >
                        {segment?.name || t('Unnamed segment')}
                    </Typography>
                </Box>

                <Tabs
                    centered
                    onChange={handleOnChangeTab}
                    scrollButtons="auto"
                    value={selectedTab}
                    variant="fullWidth"
                    sx={{ flex: 1 }}
                >
                    <Tab
                        id="tab-rooftop"
                        key="tab-rooftop"
                        label={t('Rooftop')}
                        value="rooftop"
                    />
                    <Tab
                        id="tab-module"
                        key="tab-module"
                        label={t('Module')}
                        value="module"
                    />
                    <Tab
                        id="tab-groups"
                        key="tab-groups"
                        label={t('Groups')}
                        value="groups"
                    />
                </Tabs>

                <CustomTabPanel
                    key="tab-rooftop"
                    selectedTab={selectedTab}
                    value="rooftop"
                >
                    <TabRooftop
                        control={control}
                        google={google}
                        setValue={setValue}
                    />
                </CustomTabPanel>

                <CustomTabPanel
                    key="tab-module"
                    selectedTab={selectedTab}
                    value="module"
                >
                    <TabModule
                        control={control}
                        google={google}
                        offerPanels={panels}
                        setValue={setValue}
                        systemUid={system_uid}
                    />
                </CustomTabPanel>

                <CustomTabPanel
                    key="tab-groups"
                    selectedTab={selectedTab}
                    value="groups"
                >
                    <TabGroups control={control} trigger={trigger} />
                </CustomTabPanel>

                <Button
                    disabled={hasGroupsConfigErrors}
                    fullWidth
                    sx={{ '&.MuiButton-root': { mb: 0 } }}
                    type="submit"
                >
                    {t('Save')}
                </Button>

                <Button
                    fullWidth
                    onClick={() => resetForm()}
                    sx={{ '&.MuiButton-root': { mb: 0 } }}
                    variant="text"
                >
                    {t('Cancel')}
                </Button>
            </form>
        </Box>
    );
};

const mapStateToProps = createStructuredSelector({
    hasGroupsConfigErrors: selectors.getHasGroupsConfigErrors,
    initialValues: selectors.getInitialValues,
    panels: createProposalModal.selectors.getPanelsData,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPreset: (id) => dispatch(multiPanelLayoutActions.fetchPreset(id)),
    fetchSegments: (id) => dispatch(actions.initialFetching(id)),
    resetForm: () => dispatch(actions.resetForm()),
    setCurrentValues: (values) => dispatch(actions.setCurrentValues(values)),
    updateSegment: (config) => dispatch(actions.updateSegment(config)),
});

SegmentEditForm.propTypes = {
    commercialOfferId: PropTypes.string,
    fetchPreset: PropTypes.func,
    hasGroupsConfigErrors: PropTypes.bool,
    initialValues: PropTypes.object,
    onCloseDrawer: PropTypes.func,
    panels: PropTypes.array,
    resetForm: PropTypes.func,
    segment: PropTypes.object,
    setCurrentValues: PropTypes.func,
    updateSegment: PropTypes.func,
    fetchSegments: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(SegmentEditForm);
